import { FaCalendarCheck } from "react-icons/fa";
import { FiCalendar, FiClock, FiTag } from "react-icons/fi";
import type { TFunction } from "i18next";
import type { DashboardUpcomingItem } from "../types/dashboard.types";
import { formatDetailDate, formatDetailTime } from "../utils/dashboardDate.utils";
import { translateClassName } from "../../../utils/classroomUtils";

interface DashboardUpcomingDetailsProps {
  item: DashboardUpcomingItem;
  locale: string;
  t: TFunction;
  onBack: () => void;
  onOpen: () => void;
}

const DashboardUpcomingDetails = ({ item, locale, t, onBack, onOpen }: DashboardUpcomingDetailsProps) => (
  <div className="border-t border-slate-200/80 px-4 py-4 motion-safe:animate-[dashboardFadeIn_180ms_ease-out_both] dark:border-white/10">
    <button
      type="button"
      onClick={onBack}
      className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-indigo-700 transition hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-indigo-200"
    >
      ← {t("dashboard.personal.backToOverview", { defaultValue: "Back to overview" })}
    </button>
    <p className="text-lg font-bold leading-tight text-slate-950 dark:text-white">{item.title}</p>

    <dl className="mt-4 space-y-3 text-sm">
      <div className="grid grid-cols-[84px_1fr] items-start gap-3">
        <dt className="font-semibold text-slate-400">{t("common.date", { defaultValue: "Date" })}</dt>
        <dd className="flex min-w-0 items-center gap-3 font-semibold text-slate-800 dark:text-slate-100">
          <FiCalendar className="shrink-0 text-slate-400" />
          <span>{formatDetailDate(item, locale)}</span>
        </dd>
      </div>
      <div className="grid grid-cols-[84px_1fr] items-start gap-3">
        <dt className="font-semibold text-slate-400">{t("common.time", { defaultValue: "Time" })}</dt>
        <dd className="flex min-w-0 items-center gap-3 font-semibold text-slate-800 dark:text-slate-100">
          <FiClock className="shrink-0 text-slate-400" />
          <span>{formatDetailTime(item, locale, t("dashboard.all_day", { defaultValue: "All day" }))}</span>
        </dd>
      </div>
      <div className="grid grid-cols-[84px_1fr] items-start gap-3">
        <dt className="font-semibold text-slate-400">{t("common.type", { defaultValue: "Type" })}</dt>
        <dd className="flex min-w-0 items-center gap-3 font-semibold text-slate-800 dark:text-slate-100">
          <FiTag className="shrink-0 text-slate-400" />
          <span>{item.type}</span>
        </dd>
      </div>
      {(item.details.teacherName || item.details.classroomName || item.details.childName || item.details.location || item.details.description) && (
        <div className="grid grid-cols-[84px_1fr] items-start gap-3 pt-1">
          <dt className="font-semibold text-slate-400">{t("common.description", { defaultValue: "Description" })}</dt>
          <dd className="space-y-1 leading-6 text-slate-700 dark:text-slate-200">
            {item.details.teacherName ? <p>{t("common.teacher", { defaultValue: "Teacher" })}: {item.details.teacherName}</p> : null}
            {item.details.classroomName ? <p>{t("common.classroom", { defaultValue: "Classroom" })}: {translateClassName(item.details.classroomName)}</p> : null}
            {item.details.childName ? <p>{t("common.child", { defaultValue: "Child" })}: {item.details.childName}</p> : null}
            {item.details.location ? <p>{t("common.location", { defaultValue: "Location" })}: {item.details.location}</p> : null}
            {item.details.description ? <p className="whitespace-pre-wrap">{item.details.description}</p> : null}
          </dd>
        </div>
      )}
    </dl>

    {item.path ? (
      <button
        type="button"
        onClick={onOpen}
        className="mt-5 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <FaCalendarCheck />
        {item.source === "parentMeeting"
          ? t("dashboard.personal.openAppointment", { defaultValue: "Open appointment" })
          : t("dashboard.personal.openAgendaItem", { defaultValue: "Open agenda item" })}
      </button>
    ) : null}
  </div>
);

export default DashboardUpcomingDetails;
